import React from 'react'
import {Col, Row} from 'styled-bootstrap-grid'
import {isValidPhoneNumber} from 'libphonenumber-js'
import styled from 'styled-components'
import FormikFileInput from 'src/components/form/FileInput'
import {FormikRadioButton} from 'src/components/form/RadioButton'
import {FormikSelect} from 'src/components/form/Select'
import {FormikCleaveInput} from 'src/components/form'
import SsnInput from 'src/components/form/SsnInput'
import {validateEmail} from 'src/utils/validation'
import {isValidSsn} from 'src/utils/strings'
import FormikInput from '../../form/Input'

const employmentTypes = ['Skrifstofa', 'Lager', 'Sala', 'Verslun']

export interface EmploymentFormValues {
  name: string
  ssn: string
  address: string
  postalCode: number | undefined
  city: string
  phone: string
  email: string
  type: string
  job: string
  family: string
  hobbies: string
  education: string
  references: string
  hours: string
  start: string
  smoker: string
  time: string
  file: File | undefined
}

export interface EmploymentFormErrorProps {
  name?: string
  ssn?: string
  address?: string
  postalCode?: string
  city?: string
  phone?: string
  email?: string
  type?: string
  job?: string
  hobbies?: string
  education?: string
  references?: string
  hours?: string
  start?: string
  smoker?: string
  file?: string
  family?: string
}

const RadioGroup = styled.div`
  display: flex;

  label {
    margin-right: 16px;
  }
`

export function validateEmploymentFormFields({
  name,
  ssn,
  address,
  postalCode,
  city,
  phone,
  family,
  email,
  type,
  job,
  hobbies,
  education,
  references,
  hours,
  start,
  smoker,
  file,
}: EmploymentFormValues): EmploymentFormErrorProps {
  const errors: EmploymentFormErrorProps = {}

  if (!name) {
    errors.name = 'Hér vantar nafn'
  }

  if (!ssn) {
    errors.ssn = 'Hér vantar kennitölu'
  } else if (!isValidSsn(ssn)) {
    errors.ssn = 'Ógild kennitala'
  }

  if (!address) {
    errors.address = 'Hér vantar heimilisfang'
  }

  if (!postalCode) {
    errors.postalCode = 'Hér vantar póstnúmer'
  }

  if (!city) {
    errors.city = 'Hér vantar bæjarfélag'
  }

  if (!phone) {
    errors.phone = 'Hér vantar símanúmer'
  } else if (!isValidPhoneNumber(phone, 'IS')) {
    errors.phone = 'Ekki leyfilegt símanúmer'
  }

  if (!email) {
    errors.email = 'Hér vantar netfang'
  } else if (!validateEmail(email)) {
    errors.email = 'Netfang ekki á réttu formi'
  }

  if (!type) {
    errors.type = 'Hér vantar tegund starfs'
  }

  if (!job) {
    errors.job = 'Hér vantar núverandi starf'
  }

  if (!hobbies) {
    errors.hobbies = 'Hér vantar áhugamál'
  }

  if (!education) {
    errors.education = 'Hér vantar menntun'
  }

  if (!references) {
    errors.references = 'Hér vantar meðmæli'
  }

  if (!hours) {
    errors.hours = 'Hér vantar vinnutíma'
  }

  if (!start) {
    errors.start = 'Hér vantar dagsetningu'
  }

  if (!family) {
    errors.family = 'Hér vantar upplýsingar um fjölskylduhagi'
  }

  if (!smoker) {
    errors.smoker = 'Hér vantar upplýsingar um reykingar'
  }

  if (!file) {
    errors.file = 'Hér vantar ferilskrá'
  }

  return errors
}

export default function EmploymentFormFields(): JSX.Element {
  return (
    <Row>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Nafn*" name="name" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <SsnInput label="Kennitala*" name="ssn" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Heimilisfang*" name="address" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Póstnúmer*" name="postalCode" characterLimit={3} />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Bæjarfélag*" name="city" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikCleaveInput
          label="Sími*"
          name="phone"
          options={{phone: true, phoneRegionCode: 'IS'}}
        />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Netfang*" name="email" type="email" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikSelect
          name="type"
          label="Sótt um*"
          placeholder="Veldu"
          items={employmentTypes}
          itemToString={(e) => e ?? ''}
          withGutter
        />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Núverandi starf*" name="job" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Fjölskylduhagir*" name="family" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Menntun*" name="education" type="textarea" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Meðmæli*" name="references" type="textarea" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Áhugamál*" name="hobbies" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Vinnutími frá/til*" name="hours" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Hvenær getur þú byrjað?*" name="start" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <FormikInput label="Reykir þú?*" name="smoker" />
      </Col>
      <Col col={12} sm={6} lg={5}>
        <RadioGroup>
          <FormikRadioButton
            name="time"
            label="Framtíðarstarf"
            value="Framtíðarstarf"
            withGutter
            defaultChecked
          />
          <FormikRadioButton
            name="time"
            label="Tímabundið starf"
            value="Tímabundið starf"
            withGutter
          />
        </RadioGroup>
      </Col>
      <Col col={12}>
        <FormikFileInput
          label="Ferilskrá með mynd*"
          name="file"
          type="file"
          withGutter
        />
      </Col>
    </Row>
  )
}
